import { z } from 'zod';
/**
 * Produkt BAU (Tenant gu / MLG Generalunternehmung, Produktname BASALT) — Bauprojekte,
 * Kostenstellen nach BKP, Ausschreibung/Vergabe, Werkverträge.
 *
 * Geldbeträge IMMER als Integer in Rappen (…Rappen-Suffix), Prozentsätze als
 * Dezimalzahl, Mengen als Zahl (keine Beträge). Fachvokabular deutsch,
 * Status-Werte lowercase Strings (Plattform-Konvention, vgl. Mandate).
 */
/** Business-Obergrenze CHF 1 Mrd (Review H3): Spalten sind BIGINT, die
 *  Number-Arithmetik bleibt weit unter Number.MAX_SAFE_INTEGER.
 *
 *  EXPORTIERT seit A-1 (Vermarktungs-Feed-Contract §3): der Feed nennt diese
 *  Obergrenze ausdrücklich als "BAU-`MAX_RAPPEN`" und leitet seine eigenen
 *  Rappen-Felder davon ab (`schemas/vermarktung-feed.ts`) — EINE Zahl, keine
 *  zweite Literale. Verhalten hier unverändert; nur die Sichtbarkeit. */
export const MAX_RAPPEN = 100_000_000_000;
const rappen = z.number().int().min(-MAX_RAPPEN).max(MAX_RAPPEN);
const rappenPositiv = z.number().int().nonnegative().max(MAX_RAPPEN);
const prozent = z.number().min(0).max(100);
/** Mengen-Deckel: 1e9 Einheiten reichen für jede Baustelle (Review H3). */
const menge = z.number().positive().max(1_000_000_000);
// ── Projekt ──────────────────────────────────────────────────────────────────
export const bauProjektStatusSchema = z.enum([
    'planung',
    'ausschreibung',
    'ausfuehrung',
    'abnahme',
    'garantie',
    'abgeschlossen',
]);
export const bauProjektCreateSchema = z.object({
    bezeichnung: z.string().trim().min(1).max(200),
    strasse: z.string().trim().max(200).optional(),
    plz: z.string().trim().max(10).optional(),
    ort: z.string().trim().max(100).optional(),
    // #345 BLOCKER-1: `.nullable()` ist Pflicht, nicht Kosmetik — ohne sie macht
    // `z.coerce.date()` aus einem gesendeten `null` die Epoch (01.01.1970) und
    // speichert sie als gültiges Datum. Das Update-Schema erbt via
    // `.extend().partial()`, also gilt der Fix auch fürs Leeren beim Bearbeiten.
    baubeginn: z.coerce.date().nullable().optional(),
    fertigstellung: z.coerce.date().nullable().optional(),
    /** Projektweite Standardabzüge in % der Netto-Abrechnungssumme (Anhang 2) —
     *  MLG-Standard 0.6/0.3/0.6 = 1.5%, aber pro Projekt frei vereinbar. */
    abzugBaureinigungProzent: prozent.nullable().optional(),
    abzugBauwesenversicherungProzent: prozent.nullable().optional(),
    abzugBauwasserStromProzent: prozent.nullable().optional(),
    baubewilligungDatum: z.coerce.date().nullable().optional(),
});
export const bauProjektUpdateSchema = bauProjektCreateSchema
    .extend({ status: bauProjektStatusSchema })
    .partial();
// ── Kostenstelle (BKP-Hierarchie) ────────────────────────────────────────────
export const bauKostenstelleCreateSchema = z.object({
    nummer: z.string().trim().min(1).max(20),
    bezeichnung: z.string().trim().min(1).max(200),
    budgetRappen: rappenPositiv.default(0),
    elternId: z.string().uuid().nullable().optional(),
});
/**
 * PATCH einer Kostenstelle. NICHT von `bauKostenstelleCreateSchema.partial()`
 * abgeleitet: in Zod 4 entfernt `.partial()` ein `.default()` NICHT, d.h. ein
 * Patch, der `budgetRappen` weglässt, würde `budgetRappen: 0` einspeisen und das
 * Budget stillschweigend auf null setzen. Darum explizit ohne Default deklariert
 * (Validierungsgrenzen bleiben identisch zum Create-Schema).
 */
export const bauKostenstelleUpdateSchema = z
    .object({
    nummer: z.string().trim().min(1).max(20),
    bezeichnung: z.string().trim().min(1).max(200),
    budgetRappen: rappenPositiv,
    elternId: z.string().uuid().nullable().optional(),
})
    .partial();
// ── Beteiligter (Unternehmer/Planer am Projekt) ──────────────────────────────
export const bauBeteiligterCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    rolleImProjekt: z.string().trim().min(1).max(100),
    email: z.string().trim().email().max(254).optional(),
    telefon: z.string().trim().max(40).optional(),
});
export const bauBeteiligterUpdateSchema = bauBeteiligterCreateSchema.partial();
// ── Positionsbibliothek (eigene Beschriebe — KEIN NPK, CRB-Lizenz) ───────────
export const bauPositionsVorlageSchema = z.object({
    gewerk: z.string().trim().min(1).max(100),
    nummer: z.string().trim().min(1).max(20),
    beschrieb: z.string().trim().min(1).max(500),
    einheit: z.string().trim().min(1).max(20),
});
// ── Ausschreibung ────────────────────────────────────────────────────────────
export const bauAusschreibungStatusSchema = z.enum([
    'entwurf',
    'versandt',
    'offerten_eingang',
    'vergeben',
    'abgebrochen',
]);
export const bauAusschreibungCreateSchema = z.object({
    projektId: z.string().uuid(),
    gewerk: z.string().trim().min(1).max(100),
    kostenstelleId: z.string().uuid().nullable().optional(),
    // #345 BLOCKER-1 (siehe bauProjektCreateSchema).
    eingabefrist: z.coerce.date().nullable().optional(),
});
export const bauAusschreibungUpdateSchema = z
    .object({
    gewerk: z.string().trim().min(1).max(100),
    kostenstelleId: z.string().uuid().nullable(),
    eingabefrist: z.coerce.date().nullable(),
})
    .partial();
export const bauPositionCreateSchema = z.object({
    nummer: z.string().trim().min(1).max(20),
    beschrieb: z.string().trim().min(1).max(500),
    menge,
    einheit: z.string().trim().min(1).max(20),
    einheitspreisKvRappen: rappenPositiv.nullable().optional(),
});
export const bauPositionUpdateSchema = bauPositionCreateSchema.partial();
export const bauAnfrageStatusSchema = z.enum(['erstellt', 'versandt', 'eingegangen', 'abgesagt']);
export const bauAnfrageCreateSchema = z.object({
    beteiligterId: z.string().uuid(),
});
// ── Offerte & Vergabe ────────────────────────────────────────────────────────
export const bauOfferteCreateSchema = z.object({
    beteiligterId: z.string().uuid(),
    positionen: z
        .array(z.object({ positionId: z.string().uuid(), einheitspreisRappen: rappenPositiv }))
        .min(1),
});
/** Preisart gemäss Formular «Werkvertrag erstellen»: Pauschalwerkpreis oder
 *  Abrechnung nach Ausmass — steuert später Nachtrags-/Schlussrechnungsprüfung. */
export const bauPreisartSchema = z.enum(['pauschal', 'ausmass']);
/** Sätze, die zusammen vom selben Geldbetrag abgezogen werden — ihre Summe
 *  über 100% hiesse negativer Zahlbetrag (Review #342, MAJOR-5). */
const ABZUGSSAETZE = [
    'skontoProzent',
    'rueckbehaltProzent',
    'gesamtrabattProzent',
    'allgAbzuegeProzent',
    'baureklameProzent',
];
function pruefeAbzugssumme(werte, ctx) {
    const summe = ABZUGSSAETZE.reduce((s, k) => s + (werte[k] ?? 0), 0);
    if (summe > 100) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Abzugssätze summieren sich auf ${summe}% — mehr als 100% ergäbe einen negativen Zahlbetrag.`,
            path: [],
        });
    }
}
const konditionenFelder = {
    skontoProzent: prozent.nullable().optional(),
    rueckbehaltProzent: prozent.nullable().optional(),
    garantieMonate: z.number().int().min(0).max(240).nullable().optional(),
    preisart: bauPreisartSchema.nullable().optional(),
    gesamtrabattProzent: prozent.nullable().optional(),
    allgAbzuegeProzent: prozent.nullable().optional(),
    baureklameProzent: prozent.nullable().optional(),
    spezialrabattRappen: rappenPositiv.nullable().optional(),
    anzahlungProzent: prozent.nullable().optional(),
    zahlungszielTage: z.number().int().min(0).max(365).nullable().optional(),
    akontoLimiteProzent: prozent.nullable().optional(),
    konventionalstrafeProTagRappen: rappenPositiv.nullable().optional(),
    konventionalstrafeMaxProzent: prozent.nullable().optional(),
    erfuellungsgarantieProzent: prozent.nullable().optional(),
    maengelhaftungsgarantieProzent: prozent.nullable().optional(),
    maengelhaftungsgarantieJahre: z.number().int().min(0).max(30).nullable().optional(),
    preisbindungBis: z.coerce.date().nullable().optional(),
};
/** Vergabekonditionen nach VK_2.31fII + Werkvertragsvorlage: alles optional
 *  (leer = nicht vereinbart) — die Kaskade rechnet fehlende Sätze als 0. */
export const bauVergabeSchema = z
    .object({
    offerteId: z.string().uuid(),
    kostenstelleId: z.string().uuid().optional(),
    ...konditionenFelder,
})
    .superRefine(pruefeAbzugssumme);
/** Korrekturpfad (Review #342, MAJOR-2): Konditionen nachträglich anpassen —
 *  fehlender Schlüssel = unverändert, null = «nicht vereinbart». Wirkt nur auf
 *  KÜNFTIGE Rechnungen (jede Rechnung snapshottet ihre Abzüge); die Summe der
 *  Abzugssätze prüft der Service zusätzlich GEGEN DEN GESPEICHERTEN Stand. */
export const bauKonditionenUpdateSchema = z
    .object(konditionenFelder)
    .superRefine(pruefeAbzugssumme);
// ── Werkvertrag: Nachträge & Rechnungen ──────────────────────────────────────
export const bauWerkvertragStatusSchema = z.enum([
    'entwurf',
    'unterzeichnet',
    'in_ausfuehrung',
    'abgenommen',
    'garantie',
    'abgeschlossen',
]);
export const bauNachtragCreateSchema = z.object({
    beschrieb: z.string().trim().min(1).max(500),
    /** Negativ = Minderkosten (ein Vorzeichen statt zweier Typen). */
    betragRappen: rappen.refine((v) => v !== 0, 'Betrag darf nicht 0 sein'),
});
export const bauNachtragStatusSchema = z.enum(['beantragt', 'bewilligt', 'abgelehnt']);
export const bauNachtragEntscheidSchema = z.object({
    entscheid: z.enum(['bewilligt', 'abgelehnt']),
});
export const bauRechnungStatusSchema = z.enum([
    'eingegangen',
    'geprueft',
    'freigegeben',
    'bezahlt',
    'beanstandet',
    'retourniert',
]);
/** Rechnungstypen gemäss MLG-Merkblatt «Rechnungsstellung» — Akonto ist
 *  kumulativ (jede Akontorechnung zieht die bisherigen ab). */
export const bauRechnungTypSchema = z.enum(['akonto', 'einzel', 'regie', 'schluss']);
export const bauRechnungCreateSchema = z.object({
    nummer: z.string().trim().max(50).optional(),
    typ: bauRechnungTypSchema.optional(),
    bruttoRappen: rappen.positive(),
    datum: z.coerce.date().optional(),
});
export const bauRechnungStatusUpdateSchema = z.object({
    status: bauRechnungStatusSchema,
});
export const bauWerkvertragStatusUpdateSchema = z.object({
    status: bauWerkvertragStatusSchema,
});
// ── Dokument-Generatoren ─────────────────────────────────────────────────────
/** Absenderin aller BASALT-Dokumente (Tenant gu). Adresse gemäss dem Merkblatt
 *  «Rechnungsstellung» der MLG Generalunternehmung AG — gleiches Muster wie
 *  FINAVEST_PARTY (pipeline.ts). */
export const MLG_GU_PARTY = {
    name: 'MLG Generalunternehmung AG',
    address: 'Zikadenweg 27 A, 3006 Bern',
};
/**
 * Der einzige zulässige Rechnungseingang der GU.
 *
 * QUELLE (#350 nachgeprüft): das MLG-Merkblatt «Rechnungsstellung zu
 * Honorarvertrag» (unterlagen/vertragsmanagement/…_Planer.pdf), wörtlich:
 * «Senden Sie bitte die Originalrechnung ausschliesslich an folgende
 * E-Mail-Adresse: gu-invoices@mlg-ag.ch». Die Adresse ist also NICHT erfunden
 * — sie steht als Konstante hier, damit die Herkunft am Wert klebt und die
 * Frage nicht bei jedem Dokument neu gestellt wird.
 */
export const MLG_GU_RECHNUNGSMAIL = 'gu-invoices@mlg-ag.ch';
/** CH-Normalsatz als Default für generierte Dokumente (Stand 2024: 8.1%) —
 *  pro Aufruf übersteuerbar; eine Stichtagslogik kommt mit dem weiteren
 *  Generatoren-Ausbau (die Unterlagen zeigen 7.7% UND 8.1% im Umlauf). */
export const MWST_NORMALSATZ_PROZENT = 8.1;
export const bauVergabeprotokollPdfSchema = z.object({
    mwstProzent: z.number().min(0).max(30).optional(),
    /** Preiskorrekturen aus der Verhandlung (Minder negativ) — #343 minor. */
    korrekturenRappen: rappen.optional(),
});
/*
 * #349 BLOCKER-1: Das frühere `bauOfferteAuslesenSchema` (PDF als Base64 im
 * JSON-Body) ist ersatzlos entfallen. Der Upload läuft über MULTIPART
 * (`FileInterceptor`), also validiert nicht Zod die Datei, sondern die
 * Prüfkette des Hauses: multer-Grössenlimit → Magic-Bytes → Virenscan.
 */
// ── Bauleitung (SMINO-Kern): Aufgaben/Mängel, Bautagebuch, Protokolle ────────
export const bauAufgabeTypSchema = z.enum(['aufgabe', 'mangel']);
export const bauAufgabeStatusSchema = z.enum(['offen', 'in_arbeit', 'erledigt', 'abgenommen']);
export const bauAufgabePrioritaetSchema = z.enum(['hoch', 'normal', 'tief']);
export const bauAufgabeCreateSchema = z.object({
    projektId: z.string().uuid(),
    typ: bauAufgabeTypSchema.default('aufgabe'),
    titel: z.string().trim().min(1).max(200),
    beschrieb: z.string().trim().max(2000).optional(),
    ort: z.string().trim().max(200).optional(),
    kostenstelleId: z.string().uuid().nullable().optional(),
    werkvertragId: z.string().uuid().nullable().optional(),
    verantwortlicherId: z.string().uuid().nullable().optional(),
    frist: z.coerce.date().nullable().optional(),
    prioritaet: bauAufgabePrioritaetSchema.default('normal'),
});
/**
 * #346 BLOCKER-1 — Zod 4: `.partial()` STRIPPT `.default()` NICHT. Ein aus dem
 * Create-Schema abgeleitetes Update-Schema injiziert seine Defaults deshalb in
 * JEDE Teil-PATCH: `parse({titel})` lieferte `{titel, typ:'aufgabe',
 * prioritaet:'normal'}` — und der Service schreibt `data: dto` direkt, also
 * degradierte ein Titel-Update einen MANGEL still zur Aufgabe. Update-Schemas
 * werden darum explizit gebaut, nie abgeleitet; der Regressionstest in
 * `bau.test.ts` hält `parse({titel})` auf genau einen Schlüssel fest.
 */
export const bauAufgabeUpdateSchema = z
    .object({
    typ: bauAufgabeTypSchema,
    titel: z.string().trim().min(1).max(200),
    beschrieb: z.string().trim().max(2000).nullable(),
    ort: z.string().trim().max(200).nullable(),
    kostenstelleId: z.string().uuid().nullable(),
    werkvertragId: z.string().uuid().nullable(),
    verantwortlicherId: z.string().uuid().nullable(),
    frist: z.coerce.date().nullable(),
    prioritaet: bauAufgabePrioritaetSchema,
})
    .partial();
export const bauAufgabeStatusUpdateSchema = z.object({ status: bauAufgabeStatusSchema });
export const bauTagebuchCreateSchema = z.object({
    projektId: z.string().uuid(),
    datum: z.coerce.date(),
    wetter: z.string().trim().max(100).optional(),
    temperatur: z.string().trim().max(40).optional(),
    /** Firmen mit Mannschaftsstärke — KEINE Privatpersonen (revDSG-Disziplin). */
    anwesende: z.string().trim().max(1000).optional(),
    arbeiten: z.string().trim().min(1).max(4000),
    vorkommnisse: z.string().trim().max(4000).optional(),
});
/** #346 BLOCKER-1: explizit gebaut (kein `.default()`-Erbe aus dem Create). */
export const bauTagebuchUpdateSchema = z
    .object({
    datum: z.coerce.date(),
    wetter: z.string().trim().max(100).nullable(),
    temperatur: z.string().trim().max(40).nullable(),
    anwesende: z.string().trim().max(1000).nullable(),
    arbeiten: z.string().trim().min(1).max(4000),
    vorkommnisse: z.string().trim().max(4000).nullable(),
})
    .partial();
export const bauProtokollTypSchema = z.enum(['bausitzung', 'abnahme']);
export const bauBeschlussSchema = z.object({
    nr: z.number().int().min(1).max(999),
    text: z.string().trim().min(1).max(2000),
    /**
     * #346 MAJOR-6 (Minimalteil): der Verantwortliche ist eine REFERENZ auf einen
     * Beteiligten, kein Freitextname — damit greift die revDSG-Anonymisierung
     * (#229) automatisch, wie überall sonst im Produkt. Den Namen löst der
     * PDF-Renderer zum Zeitpunkt der Ausgabe auf.
     */
    verantwortlicherId: z.string().uuid().optional(),
    frist: z.string().trim().max(40).optional(),
});
export const bauProtokollCreateSchema = z.object({
    projektId: z.string().uuid(),
    typ: bauProtokollTypSchema.default('bausitzung'),
    titel: z.string().trim().max(200).optional(),
    datum: z.coerce.date(),
    /** Teilnehmende Firmen/Rollen — KEINE Privatpersonen (revDSG-Disziplin). */
    teilnehmer: z.string().trim().max(1000).optional(),
    beschluesse: z.array(bauBeschlussSchema).max(200).default([]),
});
export const bauTerminTypSchema = z.enum(['meilenstein', 'phase']);
export const bauTerminStatusSchema = z.enum(['geplant', 'in_arbeit', 'erledigt']);
export const bauTerminCreateSchema = z
    .object({
    projektId: z.string().uuid(),
    typ: bauTerminTypSchema.default('meilenstein'),
    titel: z.string().trim().min(1).max(200),
    start: z.coerce.date(),
    ende: z.coerce.date().nullable().optional(),
    kostenstelleId: z.string().uuid().nullable().optional(),
    status: bauTerminStatusSchema.default('geplant'),
    notiz: z.string().trim().max(1000).optional(),
})
    .refine((t) => !t.ende || t.ende >= t.start, 'Ende liegt vor dem Start.');
export const bauTerminUpdateSchema = z
    .object({
    typ: bauTerminTypSchema,
    titel: z.string().trim().min(1).max(200),
    start: z.coerce.date(),
    ende: z.coerce.date().nullable(),
    kostenstelleId: z.string().uuid().nullable(),
    status: bauTerminStatusSchema,
    notiz: z.string().trim().max(1000).nullable(),
})
    // Achtung: `ende >= start` lässt sich hier NICHT prüfen — eine PATCH, die nur
    // `start` verschiebt, kennt das gespeicherte `ende` nicht. Die Prüfung läuft
    // im Service gegen den zusammengeführten Stand (#351 MINOR-2).
    .partial();
export const bauChecklistPunktSchema = z.object({
    nr: z.number().int().min(1).max(999),
    text: z.string().trim().min(1).max(500),
    status: z.enum(['offen', 'ok', 'mangel']).default('offen'),
    bemerkung: z.string().trim().max(500).optional(),
    /**
     * #351 MAJOR-3: die Spur der Mangel-Übernahme lebt am Punkt selbst. Nur so
     * ist «als Mangel übernehmen» idempotent — der zweite Klick (Doppelklick,
     * Retry, zwei Bauleiter auf demselben Punkt) findet die Aufgabe hier und
     * legt keine zweite an.
     */
    uebernommenAufgabeId: z.string().uuid().nullable().optional(),
});
export const bauChecklisteCreateSchema = z
    .object({
    projektId: z.string().uuid(),
    typ: z.enum(['checkliste', 'inspektion']).default('checkliste'),
    titel: z.string().trim().min(1).max(200),
    ort: z.string().trim().max(200).optional(),
    verantwortlicherId: z.string().uuid().nullable().optional(),
    frist: z.coerce.date().nullable().optional(),
    punkte: z.array(bauChecklistPunktSchema).min(1).max(200),
})
    // #351 MINOR-3: `nr` ist der Schlüssel, über den jedes Abhaken den Punkt
    // findet. Doppelte Nummern liessen einen Klick zwei Punkte umschreiben —
    // und «als Mangel übernehmen» traf immer nur den ersten.
    .superRefine((c, ctx) => {
    const nrs = c.punkte.map((pt) => pt.nr);
    const doppelt = nrs.find((nr, i) => nrs.indexOf(nr) !== i);
    if (doppelt !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['punkte'],
            message: `Punkt-Nummer ${doppelt} ist doppelt vergeben.`,
        });
    }
});
export const bauChecklistPunktUpdateSchema = z.object({
    nr: z.number().int().min(1).max(999),
    status: z.enum(['offen', 'ok', 'mangel']),
    bemerkung: z.string().trim().max(500).optional(),
});
/**
 * #346 BLOCKER-1 — der teuerste Fall: das abgeleitete Schema erbte
 * `beschluesse: [...].default([])`, also injizierte JEDE PATCH ohne
 * `beschluesse` ein leeres Array — der «Verabschieden»-Knopf sendet exakt
 * `{status:'verabschiedet'}` und löschte damit alle Beschlüsse der Bausitzung
 * im Moment ihrer Feststellung. Explizit gebaut, kein Default.
 */
export const bauProtokollUpdateSchema = z
    .object({
    titel: z.string().trim().max(200).nullable(),
    datum: z.coerce.date(),
    teilnehmer: z.string().trim().max(1000).nullable(),
    beschluesse: z.array(bauBeschlussSchema).max(200),
    status: z.enum(['entwurf', 'verabschiedet']),
})
    .partial();
// ── Eigentümerportal ─────────────────────────────────────────────────────────
/**
 * Zugang der Bauherrschaft zu einem Bauprojekt. Die E-Mail ist der Schlüssel:
 * das Kundenkonto wird über den bestehenden Einladungsweg des Hauses angelegt
 * oder gefunden, dieses Schema erfindet keinen zweiten.
 */
export const bauZugangCreateSchema = z.object({
    email: z.string().trim().email().max(254),
    /** Wie die Bauherrschaft im CRM heisst (z. B. «Familie Meier», «STWEG Ittigen»). */
    bezeichnung: z.string().trim().max(200).optional(),
});
